import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { FloorPlanObject, FloorPlanSaveConflict } from '@peles/shared';
import { bulkUpsertObjects, saveConflictOf } from '../../api/floor-plans';
import { useQueryClient } from '@tanstack/react-query';
import { itemsToObjectsPayload } from './helpers';
import type { CanvasItem } from './types';

/**
 * Everything about getting the canvas into the database, in one place.
 *
 * - **Dirty tracking that survives a save in flight.** Every edit bumps a
 *   counter; a save only marks the plan clean if nothing was edited while it
 *   was on the wire. Clearing a boolean on success dropped any edit made during
 *   the round trip from the autosave, so it waited for a manual save.
 * - **One save at a time.** Saves are serialised: one asked for while another
 *   is running waits for it and then saves the latest canvas, carrying the
 *   version the first one produced. Two overlapping saves otherwise raced, and
 *   with version checks the second refused itself.
 * - **Stale saves are stopped, not lost.** Each save names the version the
 *   canvas was loaded or last saved at. The server refuses one made from an
 *   older version (another tab, another producer) and the hook holds the
 *   conflict: autosave pauses, the canvas is untouched, and the page offers
 *   to load their version or keep this one (`overwrite`).
 * - **Ids come back.** The server assigns ids to new objects; `onSaved`
 *   receives the stored objects so the page can adopt them by `uid`.
 */

export type SaveKind = 'MANUAL' | 'AUTO' | 'QUIET';

export interface UseFloorPlanSaveOptions {
  planId: string | null;
  /** The canvas as it is now — read at save time, never a stale closure. */
  getItems: () => CanvasItem[];
  /** The stored objects after a save, to adopt server ids by uid. */
  onSaved?: (objects: FloorPlanObject[]) => void;
  /** Called with a short status line for the toast. */
  onStatus?: (message: string, isError?: boolean) => void;
}

export interface FloorPlanSaveState {
  isDirty: boolean;
  isSaving: boolean;
  conflict: FloorPlanSaveConflict | null;
  lastSavedAt: Date | null;
  autoSaveEnabled: boolean;
  autoSaveIntervalSec: number;
  setAutoSaveEnabled: Dispatch<SetStateAction<boolean>>;
  setAutoSaveIntervalSec: Dispatch<SetStateAction<number>>;
  /** An edit happened. */
  markDirty: () => void;
  /** Plan `planId` was (re)loaded at `version`; the canvas is clean unless `dirty`. */
  loaded: (planId: string, version: number, dirty?: boolean) => void;
  /** The version the canvas is based on (for restore and Save As). */
  baseVersion: () => number | undefined;
  /** Save now. Resolves true when the canvas reached the database. */
  save: (kind?: SaveKind, options?: SaveOptions) => Promise<boolean>;
  /**
   * Whether an edit has not reached the database, as of this moment — read
   * from the edit counter, not from render state, so an edit made while a save
   * was in flight counts even before the page re-renders.
   */
  hasUnsavedEdits: () => boolean;
  /** Resolve a conflict by writing this canvas over the other version. */
  overwrite: () => Promise<boolean>;
  /** Forget a conflict (the page reloaded the other version). */
  clearConflict: () => void;
}

export interface SaveOptions {
  /** Name for the kept copy of a MANUAL save. */
  label?: string;
  /**
   * The canvas to save, when it was just set and has not rendered yet. Edits
   * are counted from the moment the save is asked for, so an edit made while
   * it waits in the queue keeps the plan dirty.
   */
  items?: CanvasItem[];
  /**
   * The plan to save into (Save As), which the canvas must just have been
   * `loaded` as. Such a save is a copy of `items` into that plan: it is
   * bound to the plan and version at the moment it is asked for and is sent
   * even if the producer has moved to another plan while it waits — a copy
   * dropped from the queue would leave the new plan empty.
   */
  planId?: string;
}

interface SaveRequest extends SaveOptions {
  kind: SaveKind;
  force: boolean;
  /** Edit counter the save covers; read at run time when not given. */
  seq?: number;
  /** The load the save belongs to; read at run time when not given. */
  gen?: number;
  /** The version a targeted save was edited from, fixed when it was asked for. */
  expectedVersion?: number;
  /**
   * The plan an ordinary save was asked for on, and the canvas at that moment
   * — used only if the editor has moved to another load by the time it runs.
   */
  boundPlanId?: string | null;
  snapshot?: CanvasItem[];
}

const SAVED_MESSAGES: Record<SaveKind, string> = {
  MANUAL: 'Saved',
  AUTO: 'Auto-saved',
  QUIET: 'Saved',
};

export function useFloorPlanSave({
  planId,
  getItems,
  onSaved,
  onStatus,
}: UseFloorPlanSaveOptions): FloorPlanSaveState {
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [conflict, setConflict] = useState<FloorPlanSaveConflict | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [autoSaveIntervalSec, setAutoSaveIntervalSec] = useState(30);

  const editSeq = useRef(0);
  const savedSeq = useRef(0);
  const versionRef = useRef<number | undefined>(undefined);
  // Which plan `versionRef` belongs to. Until the selected plan has loaded,
  // saving is refused — the canvas still holds the previous plan's objects,
  // and writing them into the new one is how plans used to swap contents.
  const versionPlanRef = useRef<string | null>(null);
  // The last version each *load* was loaded or saved at, whichever canvas is
  // showing: a save bound to plan A that runs after the editor moved to B
  // still has to name A's version. Keyed by load, not plan — if A was
  // reloaded or restored meanwhile, the queued snapshot must carry its own
  // load's version so the server refuses it, never the restore's.
  const versionByLoad = useRef(new Map<number, number>());
  // Which plan each load was of — a save's version belongs to its load only
  // when it wrote that plan.
  const planByLoad = useRef(new Map<number, string>());
  // Bumped on every load. An answer is applied only to the load it was sent
  // from: the plan id alone cannot tell a canvas that left plan A and came
  // back (or had a version restored) from the one that sent the save.
  const loadGen = useRef(0);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const conflictRef = useRef<FloorPlanSaveConflict | null>(null);
  // Latest callbacks, so a queued save never runs an old render's closures.
  const latest = useRef({ planId, getItems, onSaved, onStatus });
  latest.current = { planId, getItems, onSaved, onStatus };

  const markDirty = useCallback(() => {
    editSeq.current += 1;
    setIsDirty(true);
  }, []);

  const loaded = useCallback((loadedPlanId: string, version: number, dirty = false) => {
    versionRef.current = version;
    versionPlanRef.current = loadedPlanId;
    loadGen.current += 1;
    versionByLoad.current.set(loadGen.current, version);
    planByLoad.current.set(loadGen.current, loadedPlanId);
    editSeq.current += 1;
    savedSeq.current = dirty ? editSeq.current - 1 : editSeq.current;
    conflictRef.current = null;
    setConflict(null);
    setIsDirty(dirty);
  }, []);

  const clearConflict = useCallback(() => {
    conflictRef.current = null;
    setConflict(null);
  }, []);

  const runSave = useCallback(
    async (req: SaveRequest): Promise<boolean> => {
      const { getItems: read, onSaved: adopt, onStatus: status } = latest.current;
      const targeted = req.planId !== undefined;
      /*
       * ⚠️ **An ordinary save belongs to the plan it was asked for on.** It
       * used to read the plan when it ran, so a save of plan A waiting behind
       * another one, run after the editor had switched to B, wrote B instead
       * and A's latest edits were never saved. It is bound when asked for
       * (`enqueue`); if the canvas is still that load it saves the canvas as
       * it is now, otherwise the snapshot taken then, at A's own version.
       */
      const id = targeted
        ? req.planId
        : req.boundPlanId !== undefined
          ? req.boundPlanId
          : versionPlanRef.current === latest.current.planId
            ? latest.current.planId
            : null;
      if (!id) return false;
      const gen = req.gen ?? loadGen.current;
      // The canvas may have been loaded again by the time the answer arrives
      // (another plan, the same plan reloaded, a version restored): the answer
      // is then about a canvas that is gone, and nothing of it may be applied.
      const stillShowing = () => loadGen.current === gen;
      // While a conflict stands only an explicit overwrite may write — this
      // canvas's conflict, which a copy bound to an earlier load is not under.
      if (conflictRef.current && !req.force && stillShowing()) return false;
      const showingAtStart = stillShowing();
      const items = targeted || showingAtStart ? (req.items ?? read()) : req.snapshot;
      if (!items) return false;
      const expectedVersion = req.force
        ? conflictRef.current?.currentVersion
        : targeted
          ? req.expectedVersion
          : showingAtStart
            ? versionRef.current
            : versionByLoad.current.get(gen);
      const seqAtStart = req.seq ?? editSeq.current;
      setIsSaving(true);
      try {
        const result = await bulkUpsertObjects(id, {
          objects: itemsToObjectsPayload(items),
          expectedVersion,
          ...(req.kind === 'QUIET' ? {} : { snapshot: req.kind }),
          ...(req.label ? { snapshotLabel: req.label } : {}),
        });
        queryClient.invalidateQueries({ queryKey: ['floor-plans'] });
        // Recorded against the load only when it wrote that load's own plan:
        // the Save As copy into the plan the canvas was just loaded as does,
        // a copy made from one plan into another does not.
        if (planByLoad.current.get(gen) === id) versionByLoad.current.set(gen, result.version);
        if (!stillShowing()) return true;
        versionRef.current = result.version;
        conflictRef.current = null;
        setConflict(null);
        savedSeq.current = seqAtStart;
        if (editSeq.current === seqAtStart) setIsDirty(false);
        setLastSavedAt(new Date());
        adopt?.(result.objects);
        status?.(SAVED_MESSAGES[req.kind]);
        return true;
      } catch (err) {
        if (!stillShowing()) {
          // Not this canvas's problem to hold, but not a silent loss either.
          status?.('Changes to the previous plan were not saved', true);
          return false;
        }
        const refused = saveConflictOf(err);
        if (refused) {
          conflictRef.current = refused;
          setConflict(refused);
          status?.('Not saved — this plan was changed elsewhere', true);
        } else {
          status?.(
            req.kind === 'AUTO'
              ? 'Auto-save failed — changes not saved'
              : 'Save failed — changes not saved',
            true,
          );
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient],
  );

  const enqueue = useCallback(
    (req: SaveRequest): Promise<boolean> => {
      // A save of explicit items covers the edits made up to now, not up to
      // whenever the queue reaches it.
      let request = req.items ? { ...req, seq: editSeq.current } : req;
      if (req.planId !== undefined) {
        request = { ...request, gen: loadGen.current, expectedVersion: versionRef.current };
      } else if (!req.force) {
        // Bound to the plan and load showing now, with the canvas as it is
        // now in case the editor has moved on by the time the queue reaches it.
        const current = versionPlanRef.current;
        const boundPlanId = current !== null && current === latest.current.planId ? current : null;
        request = {
          ...request,
          boundPlanId,
          gen: loadGen.current,
          snapshot: req.items ?? latest.current.getItems(),
        };
      }
      const previous = inFlight.current;
      const next = (previous ?? Promise.resolve(true))
        .catch(() => false)
        .then(() => runSave(request));
      inFlight.current = next;
      void next.finally(() => {
        if (inFlight.current === next) inFlight.current = null;
      });
      return next;
    },
    [runSave],
  );

  const save = useCallback(
    (kind: SaveKind = 'MANUAL', options: SaveOptions = {}) =>
      enqueue({ ...options, kind, force: false }),
    [enqueue],
  );
  const overwrite = useCallback(() => enqueue({ kind: 'MANUAL', force: true }), [enqueue]);
  const baseVersion = useCallback(() => versionRef.current, []);

  // Autosave: on the interval, when there is something unsaved and no conflict.
  useEffect(() => {
    if (!autoSaveEnabled || !planId || !isDirty || conflict) return;
    const timer = setInterval(() => {
      if (editSeq.current !== savedSeq.current && !inFlight.current) void save('AUTO');
    }, autoSaveIntervalSec * 1000);
    return () => clearInterval(timer);
  }, [autoSaveEnabled, autoSaveIntervalSec, planId, isDirty, conflict, save]);

  // Leaving the page with unsaved work asks first.
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const hasUnsavedEdits = useCallback(() => editSeq.current !== savedSeq.current, []);

  return {
    isDirty,
    isSaving,
    conflict,
    lastSavedAt,
    autoSaveEnabled,
    autoSaveIntervalSec,
    setAutoSaveEnabled,
    setAutoSaveIntervalSec,
    markDirty,
    loaded,
    baseVersion,
    save,
    hasUnsavedEdits,
    overwrite,
    clearConflict,
  };
}

/** Copy server ids onto canvas items by uid; the same array when nothing changed. */
export function adoptServerIds(items: CanvasItem[], objects: FloorPlanObject[]): CanvasItem[] {
  const idByUid = new Map<string, string>();
  objects.forEach((o) => {
    const uid = (o.config as Record<string, unknown> | null)?.uid;
    if (typeof uid === 'string') idByUid.set(uid, o.id);
  });
  let changed = false;
  const next = items.map((it) => {
    const id = idByUid.get(it.uid);
    if (!id || id === it.id) return it;
    changed = true;
    return { ...it, id };
  });
  return changed ? next : items;
}
